/**
 * Supabase client + 認證(Google / email 魔法連結)。
 *
 * 雲端帳號用來「把每個小孩的學習進度綁到登入帳號、跨裝置、不同人切開」。
 * **env flag 包著**:`VITE_SUPABASE_URL` + `VITE_SUPABASE_ANON_KEY` 沒設時,
 * `isCloudEnabled()` 回 false、所有函式 no-op → app 完全照舊用本機 profile。
 * 設定步驟見 docs/SUPABASE_SETUP.md;schema 見 supabase/schema.sql。
 *
 * anon key 是設計來放前端的(可公開、受 RLS 保護),不是祕密。
 */
import { createClient, type SupabaseClient, type User } from '@supabase/supabase-js';

function config(): { url: string; key: string } | null {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  return url && key ? { url, key } : null;
}

let _client: SupabaseClient | null | undefined;

/** 取得(快取的)Supabase client;雲端未設定時回 null。 */
export function getSupabase(): SupabaseClient | null {
  if (_client !== undefined) return _client;
  const c = config();
  _client = c
    ? createClient(c.url, c.key, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      })
    : null;
  return _client;
}

/** 雲端是否已設定(沒設 → 完全照舊用本機 profile,不影響現有流程)。 */
export function isCloudEnabled(): boolean {
  return config() !== null;
}

/** Google 登入(OAuth 轉跳;回來後 detectSessionInUrl 會接住 session)。 */
export async function signInWithGoogle(): Promise<{ error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { error: 'cloud-disabled' };
  const { error } = await sb.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: location.origin },
  });
  return { error: error?.message ?? null };
}

/** Email 魔法連結登入(零外部設定;Google 設好前可先用這個)。 */
export async function signInWithEmail(email: string): Promise<{ error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { error: 'cloud-disabled' };
  const { error } = await sb.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: location.origin },
  });
  return { error: error?.message ?? null };
}

export async function signOut(): Promise<void> {
  const sb = getSupabase();
  if (sb) await sb.auth.signOut();
}

/** 目前登入的使用者(家庭帳號);未登入 / 未設定回 null。 */
export async function getCurrentUser(): Promise<User | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getUser();
  return data.user ?? null;
}

/** 訂閱登入狀態變化;回傳取消訂閱函式。 */
export function onAuthChange(cb: (user: User | null) => void): () => void {
  const sb = getSupabase();
  if (!sb) return () => {};
  const { data } = sb.auth.onAuthStateChange((_event, session) => cb(session?.user ?? null));
  return () => data.subscription.unsubscribe();
}
